/* eslint-disable react/jsx-wrap-multilines */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { change, reduxForm, reset, submit } from 'redux-form';
import { useLocation } from 'react-router';
import { ModalWindow } from '../../../../semantic-ui/components/modal-window';
import ModalFooterButtons from '../../../../semantic-ui/components/modal-window/modal-footer-buttons';
import { validate } from './utils';
import { FORM_NAME } from './constants';
import { PlanningModalForm } from './planning-modal-form';
import { removeModalQuery } from '../../../../utils/removeModalQuery';
import {
	getAutoparksAction,
	getConfigsAction,
	getModesAction,
	getOrderGroupInfoAction,
	getTariffTypesAction,
	getTransportTypesAction,
	planningAction,
} from './actions';
import { useInjectSaga } from '../../../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../../../utils/injectReducer';
import reducer from './reducer';
import {
	getConfigsSelector,
	getIsConfigsLoadingSelector,
	getIsOrderGroupInfoLoadingSelector,
	getIsTransportTypesLoadingSelector,
	getTransportTypesSelector,
	getFormInitialValuesSelector,
	getIsPlanningLoadingSelector,
	getFormValuesSelector,
	getModesSelector,
	getIsModesLoadingSelector,
	getTariffTypesSelector,
	getIsTariffTypesLoadingSelector,
	getAutoparksSelector,
	getIsAutoparksLoadingSelector,
} from './selectors';
import { getProcessInfoAction, getPlanningsProcessListAction } from '../../../orders-plannings-process/actions';

const PlanningModalWrapper = ({
	onFormSubmit,
	onResetForm,
	onPlanning,
	onGetConfigs,
	onGetTransportTypes,
	isConfigsLoading,
	isTransportTypeLoading,
	configsOptions,
	transportTypesOptions,
	isModalShown,
	setIsModalShown,
	group,
	process,
	isOrderGroupInfoLoading,
	onGetOrderGroupInfo,
	isPlanningLoading,
	onFormFieldChange,
	formValues,
	onGetModes,
	modes,
	isModesLoading,
	onGetPlanningsProcessList,
	onGetProcessInfo,
	onGetTariffTypes,
	tariffTypesOptions,
	isTariffTypesLoading,
	onGetAutoparks,
	autoparksOptions,
	isAutoparksLoading,
}) => {
	if (!isModalShown) return null;
	useInjectSaga({ key: 'orderGroupsPlanningContainerSaga', saga });
	useInjectReducer({ key: 'orderGroupsPlanningContainerReducer', reducer });

	const { search } = useLocation();

	const query = new URLSearchParams(search);

	const loading = isConfigsLoading || isTransportTypeLoading || isOrderGroupInfoLoading || isPlanningLoading;

	useEffect(() => {
		if (isModalShown) {
			if (process) {
				onGetOrderGroupInfo({ id: process.group.id });
			} else if (group) {
				onGetOrderGroupInfo({ id: group.id });
			}
			onGetModes();
			onGetConfigs();
			onGetTransportTypes();
			onGetTariffTypes();
			onGetAutoparks();
		}
	}, [isModalShown, group?.id, process?.id]);

	const handleModalCancel = () => {
		onResetForm(FORM_NAME);
		setIsModalShown(false);

		if (query.has('modal', 'plannings-process')) {
			onGetPlanningsProcessList({
				process_id: query.get('id'),
				searchQuery: removeModalQuery(query.toString()),
			});
			onGetProcessInfo({ process_id: query.get('id') });
		}
	};

	const handleFormSubmit = async () => {
		await onFormSubmit(FORM_NAME);

		onPlanning({
			searchQuery: removeModalQuery(query.toString()),
			group,
			process,
			callback: handleModalCancel,
		});
	};

	const handleKeyDown = e => {
		if (e.key === 'Enter' && e.shiftKey === false) {
			handleFormSubmit();
		}
	};

	const handleCheckboxChange = (field, value) => {
		if (field === 'is_pickup_day_to_day') {
			onFormFieldChange(FORM_NAME, 'is_flexible_date', false);
			onFormFieldChange(FORM_NAME, 'is_weekend_pickup', value);
			onFormFieldChange(FORM_NAME, 'is_weekend_drop', true);
		}
		if (field === 'is_flexible_date') {
			onFormFieldChange(FORM_NAME, 'is_pickup_day_to_day', false);
			onFormFieldChange(FORM_NAME, 'is_weekend_drop', !value);
			onFormFieldChange(FORM_NAME, 'is_weekend_pickup', false);
		}
	};

	const handleCreatedDateChange = value => {
		if (value === false) {
			onFormFieldChange(FORM_NAME, 'build_days', null);
		} else if (value === true) {
			onFormFieldChange(FORM_NAME, 'is_start_date', null);
			onFormFieldChange(FORM_NAME, 'start_date', null);
		}
	};

	const handleStartDateChange = value => {
		if (value === false) {
			onFormFieldChange(FORM_NAME, 'start_date', null);
		} else if (value === true) {
			onFormFieldChange(FORM_NAME, 'is_created_date', null);
			onFormFieldChange(FORM_NAME, 'build_days', null);
		}
	};

	const handleCompatibilityAdd = () => {
		const currentCompatibilities = formValues.compatibilities || [];

		const newCompatibilities = [...currentCompatibilities, {}];

		onFormFieldChange(FORM_NAME, 'compatibilities', newCompatibilities);
	};

	const handleAllowWayChange = value => {
		if (value === false) {
			onFormFieldChange(FORM_NAME, 'penalty_cost', null);
		}
	};

	return (
		<ModalWindow
			isModalShown={isModalShown}
			onClose={handleModalCancel}
			onKeyDown={handleKeyDown}
			headerText="Маршрутизация"
			style={{ width: '895px' }}
			actionButtons={[{ type: 'close', onClick: handleModalCancel, text: 'planning' }]}
			modalContent={
				<PlanningModalForm
					modes={modes}
					isModesLoading={isModesLoading}
					configsOptions={configsOptions}
					isConfigsLoading={isConfigsLoading}
					transportTypesOptions={transportTypesOptions}
					isTransportTypeLoading={isTransportTypeLoading}
					onCheckboxChange={handleCheckboxChange}
					onStartDateChange={handleStartDateChange}
					onCreatedDateChange={handleCreatedDateChange}
					formValues={formValues}
					onCompatibilityAdd={handleCompatibilityAdd}
					tariffTypesOptions={tariffTypesOptions}
					isTariffTypesLoading={isTariffTypesLoading}
					autoparksOptions={autoparksOptions}
					isAutoparksLoading={isAutoparksLoading}
					onAllowWayChange={handleAllowWayChange}
				/>
			}
			footerButtons={
				<ModalFooterButtons
					rightButtons={[
						{
							onClick: handleFormSubmit,
							text: 'Отправить',
							color: 'success',
							disabled: loading,
							loading,
						},
						{
							onClick: handleModalCancel,
							text: 'Отмена',
							color: 'secondary',
							disabled: loading,
						},
					]}
				/>
			}
		/>
	);
};

const withForm = reduxForm({
	form: FORM_NAME,
	enableReinitialize: true,
	onSubmit: validate,
})(PlanningModalWrapper);

const mapStateToProps = createStructuredSelector({
	isConfigsLoading: getIsConfigsLoadingSelector(),
	isTransportTypeLoading: getIsTransportTypesLoadingSelector(),
	configsOptions: getConfigsSelector(),
	transportTypesOptions: getTransportTypesSelector(),
	isOrderGroupInfoLoading: getIsOrderGroupInfoLoadingSelector(),
	initialValues: getFormInitialValuesSelector(),
	isPlanningLoading: getIsPlanningLoadingSelector(),
	formValues: getFormValuesSelector(),
	modes: getModesSelector(),
	isModesLoading: getIsModesLoadingSelector(),
	tariffTypesOptions: getTariffTypesSelector(),
	isTariffTypesLoading: getIsTariffTypesLoadingSelector(),
	autoparksOptions: getAutoparksSelector(),
	isAutoparksLoading: getIsAutoparksLoadingSelector(),
});

const mapDispatchToProps = {
	onResetForm: reset,
	onFormSubmit: submit,
	onPlanning: planningAction,
	onGetConfigs: getConfigsAction,
	onGetTransportTypes: getTransportTypesAction,
	onGetOrderGroupInfo: getOrderGroupInfoAction,
	onFormFieldChange: change,
	onGetPlanningsProcessList: getPlanningsProcessListAction,
	onGetProcessInfo: getProcessInfoAction,
	onGetModes: getModesAction,
	onGetTariffTypes: getTariffTypesAction,
	onGetAutoparks: getAutoparksAction,
};

export const PlanningModalComponent = connect(mapStateToProps, mapDispatchToProps)(withForm);

PlanningModalWrapper.propTypes = {
	onFormSubmit: PropTypes.func.isRequired,
	onResetForm: PropTypes.func.isRequired,
	onPlanning: PropTypes.func.isRequired,
	onGetConfigs: PropTypes.func.isRequired,
	onGetTransportTypes: PropTypes.func.isRequired,
	isConfigsLoading: PropTypes.bool,
	isTransportTypeLoading: PropTypes.bool,
	configsOptions: PropTypes.array.isRequired,
	transportTypesOptions: PropTypes.array.isRequired,
	isModalShown: PropTypes.bool,
	setIsModalShown: PropTypes.func,
	group: PropTypes.object,
	process: PropTypes.object,
	isOrderGroupInfoLoading: PropTypes.bool,
	onGetOrderGroupInfo: PropTypes.func.isRequired,
	isPlanningLoading: PropTypes.bool,
	isCreatedDate: PropTypes.bool,
	onFormFieldChange: PropTypes.func.isRequired,
	formValues: PropTypes.object.isRequired,
	onGetPlanningsProcessList: PropTypes.func.isRequired,
	onGetProcessInfo: PropTypes.func.isRequired,
	onGetModes: PropTypes.func.isRequired,
	modes: PropTypes.array.isRequired,
	isModesLoading: PropTypes.bool,
	onGetTariffTypes: PropTypes.func.isRequired,
	tariffTypesOptions: PropTypes.array.isRequired,
	isTariffTypesLoading: PropTypes.bool,
	onGetAutoparks: PropTypes.func.isRequired,
	autoparksOptions: PropTypes.array.isRequired,
	isAutoparksLoading: PropTypes.bool,
};
